import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ProgramService } from '../../../../services/vpp/program.service';
import {forkJoin, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {Program, Programs, UIDispatch} from '../../../../domain/cloud-topology';
import {ResourceService} from '../../../../services/resource.service';
import {ProgramEditModalComponent} from '../../program-edit-modal/program-edit-modal.component';
import {MatPaginator} from "@angular/material/paginator";
import {MatTableDataSource} from "@angular/material/table";
import {MatSort} from "@angular/material/sort";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";
import {saveStatus} from "../../../../domain/constants";

export interface ProgramData {
  program: Program;
  programName: string;
  nominatedKw: number;
  marketAuction: boolean;
  pointOfMeasurement: string;
  notificationTimeframe: number;
  maxOutput?: number;
  maxEnergy?: number;
}

@Component({
    selector: 'app-program-index',
    templateUrl: './program-index.component.html',
    standalone: false
})
export class ProgramIndexComponent implements OnInit, OnChanges, OnDestroy {
  @Input() programs: Program[];
  @Output() newProgramEmitter = new EventEmitter<Program>();
  @Output() removedProgramEmitter = new EventEmitter<Program>();
  programData: ProgramData[] = [];
  requestsCompleted: boolean = false;
  private subject$: Subject<void>;
  displayedColumns: string[] = ['programName', 'maxOutput', 'maxEnergy', 'nominatedKw', 'pointOfMeasurement', 'marketAuction', 'notificationTimeframe', 'actions'];
  dataSource: MatTableDataSource<ProgramData>;

  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;

  constructor(private programService: ProgramService,
              private snackBar: MatSnackBar,
              private dialogService: MatDialog) {
    this.dataSource = new MatTableDataSource<ProgramData>();
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['programs']['currentValue'] !== undefined) {
      this.programs = changes['programs']['currentValue'];

      this.programs = Programs.sortAlphabetically(this.programs);
      this.setData();
    }
  }

  setData(): void {
    this.programData = [];
    this.subject$ = new Subject<void>();
    for (const program of this.programs) {
      this.programData.push({
        program: program,
        programName: program.name,
        nominatedKw: program.properties.nominatedKw,
        marketAuction: program.properties.marketAuction,
        pointOfMeasurement: program.properties.pointOfMeasurement,
        notificationTimeframe: program.properties.notificationTimeframe,
      });
      forkJoin([
        this.programService.getMaxOutput(program),
        this.programService.getMaxEnergy(program)
      ]).subscribe(data => {
        this.programData.find((programData) => {
          if (programData.program.id == program.id) {
            programData.maxOutput = +UIDispatch.cleanNumericOutput(data[0]);
            programData.maxEnergy = +UIDispatch.cleanNumericOutput(data[1]);
          }
        });
      }, () => {
        this.programData.find((programData) => {
          if (programData.program.id == program.id) {
            programData.maxOutput = null;
            programData.maxEnergy = null;
          }
        });

      }, () => {
        if (this.programs.indexOf(program) == (this.programs.length - 1)) {
          this.setTableData();
        }
      });
    }

  }

  ngOnDestroy(): void {
    this.subject$.next();
    this.subject$.complete();
  }

  private setTableData(): void {
    this.requestsCompleted = true;
    this.programData.sort((a, b) => {
      if(a.program.name.toLowerCase() < b.program.name.toLowerCase()) {
        return -1;
      }

      if(a.program.name.toLowerCase() > b.program.name.toLowerCase()) {
        return 1;
      }

      return 0;
    });

    this.dataSource = new MatTableDataSource<ProgramData>(this.programData);
    setTimeout(() => {
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    });
  }

  addProgram(): void {
    const dialogRef = this.dialogService.open(ProgramEditModalComponent, {
      width: '1000px',
      data: {
        program: false,
      }
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data.status == saveStatus) {
        const programForm = data.program;
        const program = {
          name: programForm.name,
          programType: programForm.programType,
          properties: {
            programPenalty: programForm.penalty,
            performanceTarget: programForm.performanceTarget,
            marketAuction: (programForm.marketAuction == 'true'), // we don't want this to be explicit equality
            notificationTimeframe: programForm.notificationTimeframe,
            programIncentive: programForm.incentive,
            pointOfMeasurement: programForm.pointOfMeasurement,
            nominatedKw: programForm.nominatedKw,
            forwardStats: programForm.forwardStats,
            forceMaxDispatch: programForm.forceMaxDispatch,
          },
        };
        this.requestsCompleted = false;
        this.programService.postProgram(program).pipe(takeUntil(this.subject$)).subscribe(programData => {
          this.programs.push(programData);
          this.programData.unshift({
            programName: program.name,
            nominatedKw: program.properties.nominatedKw,
            marketAuction: program.properties.marketAuction,
            pointOfMeasurement: program.properties.pointOfMeasurement,
            notificationTimeframe: program.properties.notificationTimeframe,
            program: programData,
            maxOutput: 0,
            maxEnergy: 0,
          });
          this.dataSource.data = this.programData;
          this.dataSource.data = [...this.dataSource.data];
          this.newProgramEmitter.emit(programData);
          this.requestsCompleted = true;

          this.snackBar.open("Program has been successfully added.", "Close", {
            duration: 3000,
            horizontalPosition: "right",
            verticalPosition: "bottom"
          });
        });
      }
    });
  }

  removeProgram(programToRemove: Program): void {
    if (!window.confirm('Are you sure you wish to remove ' + programToRemove.name + '?')) {
      return;
    }
    const updatedArray = [];
    for (const program of this.programData) {
      if (program.program.id !== programToRemove.id) {
        updatedArray.push(program);
      }
    }

    this.programData = updatedArray;
    this.programService.removeProgram(programToRemove).pipe(takeUntil(this.subject$)).subscribe( () => {
      this.removedProgramEmitter.emit(programToRemove);
      this.setTableData();
      this.snackBar.open(`${programToRemove.name} has been removed.`, "Close", {
        duration: 3000,
        horizontalPosition: "right",
        verticalPosition: "bottom"
      });
    }, () => {
      this.snackBar.open("Problem removing program.", "Close", {
        duration: 3000,
        horizontalPosition: "right",
        verticalPosition: "bottom"
      });
    });
  }

  cleanProgramText(key: string, fieldType: string): string {
    return ResourceService.cleanProgramText(key, fieldType);
  }

  cleanNumericInput(element, key) {
    if (key == 'nominatedKw'){
      return UIDispatch.cleanNumericOutput(+element.program.properties[key]);
    }
  }

}
